import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
	0% { background-position: -500px 0; }
	100% { background-position: 500px 0; }
`;

export const CopilotContainer = styled.section<{
	isV1Mode: boolean;
	isEmpty?: boolean;
}>`
	background: #000e4b;
	color: #ffffff;
	border-radius: ${({ isEmpty }) => (isEmpty ? "16px" : "10px")};
	padding: ${({ isEmpty }) => (isEmpty ? "20px 24px" : "27px 27px 23px")};
	min-height: ${({ isV1Mode, isEmpty }) =>
		isEmpty ? "auto" : isV1Mode ? "223px" : "324px"};
	box-shadow: 0 3px 9px rgba(0, 14, 75, 0.08);
	width: 100%;
	display: flex;
	flex-direction: column;
	cursor: ${({ isEmpty }) => (isEmpty ? "default" : "pointer")};
	transition: min-height 0.22s ease, border-radius 0.22s ease,
		padding 0.22s ease;

	@media (max-width: 1220px) {
		padding: ${({ isEmpty }) => (isEmpty ? "16px 18px" : "20px 20px 18px")};
	}
`;

export const TopBar = styled.div<{ isEmpty?: boolean }>`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: ${({ isEmpty }) => (isEmpty ? "12px" : "18px")};
`;

export const AIBadge = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 7px;
	border-radius: 18px;
	background: #27377a;
	color: #2effb2;
	padding: 7px 13px;
	font-weight: 800;
	font-size: 12px;
	font-family: "NotoSans-Bold", sans-serif;
	line-height: 1;
`;

export const RefreshButton = styled.button`
	border: 0;
	background: transparent;
	color: #b8f1e2;
	width: 30px;
	height: 30px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	cursor: pointer;
	transition: background-color 0.2s ease;

	&:hover {
		background: rgba(255, 255, 255, 0.1);
	}

	svg {
		transition: transform 0.3s ease;
	}

	&:active svg {
		transform: rotate(360deg);
	}

	&:disabled {
		opacity: 0.55;
		cursor: wait;
	}
`;

export const HintText = styled.p`
	text-align: center;
	color: #ffffff50;
	font-size: 10px;
	margin: 17px 0 0;
	font-family: "NotoSans-Medium", sans-serif;
`;

export const EmptyGrid = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	gap: 20px;
`;

export const EmptyLeftColumn = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 12px;

	@media (max-width: 1220px) {
		gap: 8px;
	}
`;

export const EmptyRightColumn = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
`;

export const EmptyTextContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
`;

export const EmptyTitle = styled.h3`
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 24px;
	font-weight: 700;
	color: #ffffff;
	margin: 0;

	@media (max-width: 1220px) {
		font-size: 19px;
	}
`;

export const EmptySub = styled.p`
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 13px;
	color: #bad2ff;
	margin: 0;

	@media (max-width: 1220px) {
		font-size: 11px;
	}
`;

export const StartFirstEvalButton = styled.button`
	background: #00a377;
	color: #ffffff;
	border: 0;
	border-radius: 100px;
	padding: 12px 22px;
	font-family: "NotoSans-Bold", sans-serif;
	font-size: 13px;
	font-weight: 700;
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	transition: background-color 0.2s ease, transform 0.2s ease;
	outline: none;
	white-space: nowrap;

	&:hover {
		background: #00b987;
	}

	&:active {
		transform: scale(0.98);
	}

	@media (max-width: 1220px) {
		padding: 10px 16px;
		font-size: 11px;
		gap: 8px;
	}

	/* Círculo branco do ícone play */
	span {
		width: 22px;
		height: 22px;
		border-radius: 50%;
		background: #ffffff;
		display: grid;
		place-items: center;
		color: #00a377;
		flex-shrink: 0;

		@media (max-width: 1220px) {
			width: 18px;
			height: 18px;
		}

		svg {
			width: 10px;
			height: 10px;
			fill: currentColor;
			margin-left: 2px; /* Pequeno ajuste ótico para centralizar o triângulo */

			@media (max-width: 1220px) {
				width: 8px;
				height: 8px;
				margin-left: 1px;
			}
		}
	}
`;

export const InitialState = styled.div`
	min-height: 150px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 12px;
	color: #bad2ff;
	font-family: "NotoSans-Regular", sans-serif;
	font-size: 13px;

	button {
		border: 0;
		background: transparent;
		color: #2effb2;
		font: inherit;
		font-weight: 700;
		text-decoration: underline;
		cursor: pointer;
	}
`;

export const InitialSkeleton = styled.div`
	width: 100%;
	height: 26px;
	border-radius: 8px;
	background: linear-gradient(90deg, #172661 25%, #2c3d7b 50%, #172661 75%);
	background-size: 700px 100%;
	animation: ${shimmer} 1.3s infinite linear;

	&.short {
		width: 62%;
		align-self: flex-start;
	}
`;
